/**
 * features/71c/sessionUtils.ts — MER Algo Footprints v7.1C
 *
 * Read-only data derivation utilities for Phase 7.1C Executive & Audit Layer.
 *
 * All functions are pure: they accept Journal71AEntry[] (already computed) and
 * return descriptive summaries, tags, and reports.
 *
 * INVARIANTS:
 *   - No engine calls, no store writes, no ranking mutations
 *   - Output is descriptive only — never feeds back into setup/ranking/scoring
 *   - All tags are deterministic from thresholds, not adaptive
 */

import { Journal71AEntry } from '../../types';

// ─── Shared helpers ───────────────────────────────────────────────────────────

export function fmtTs(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function fmtDate(ts: number): string {
  return new Date(ts).toISOString().split('T')[0];
}

function pct(n: number, d: number): string {
  return d === 0 ? '—' : `${Math.round(n / d * 100)}%`;
}

function topN<T extends string>(
  arr: (T | null | undefined)[],
  n: number,
): Array<{ value: T; count: number }> {
  const map = new Map<T, number>();
  for (const v of arr) {
    if (v !== null && v !== undefined && v !== '') {
      map.set(v as T, (map.get(v as T) ?? 0) + 1);
    }
  }
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([value, count]) => ({ value, count }));
}

// ─── Session boundaries ───────────────────────────────────────────────────────
// A "session" = a calendar day. Entries without a date boundary = all-time.

export interface SessionWindow {
  label:   string;   // e.g. "2026-04-01"
  entries: Journal71AEntry[];
}

export function groupBySession(entries: Journal71AEntry[]): SessionWindow[] {
  const map = new Map<string, Journal71AEntry[]>();
  for (const e of entries) {
    const day = fmtDate(e.ts);
    if (!map.has(day)) map.set(day, []);
    map.get(day)!.push(e);
  }
  return [...map.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))   // newest first
    .map(([label, entries]) => ({ label, entries }));
}

// ─── Session summary ──────────────────────────────────────────────────────────

export interface SessionSummary {
  // Identity
  sessionDate:     string;
  exportedAt:      string;

  // Coverage
  symbolsMonitored: string[];
  totalActions:     number;

  // Action breakdown
  viewed:    number;
  taken:     number;
  skipped:   number;
  noted:     number;
  outcomes:  number;

  // Rates
  selectionRate:    string;   // taken / (taken + skipped)
  reviewRate:       string;   // outcome_reviewed / trade_taken
  noteRate:         string;   // note_added / (taken + skipped)
  outcomeCompletion:string;   // outcomes / taken

  // Outcomes
  wins:       number;
  losses:     number;
  breakevens: number;

  // Top patterns
  topSetupTypes: Array<{ value: string; count: number }>;
  topRegimes:    Array<{ value: string; count: number }>;
  topSymbols:    Array<{ value: string; count: number }>;
  topGamma:      Array<{ value: string; count: number }>;

  // Discipline summary
  disciplineScore: number;
  disciplineGrade: string;

  // Session tags (deterministic)
  sessionTags: string[];
}

export function buildSessionSummary(
  entries: Journal71AEntry[],
  disciplineScore: number,
  disciplineGrade: string,
): SessionSummary {
  if (!entries.length) {
    return {
      sessionDate: fmtDate(Date.now()),
      exportedAt: new Date().toISOString(),
      symbolsMonitored: [], totalActions: 0,
      viewed: 0, taken: 0, skipped: 0, noted: 0, outcomes: 0,
      selectionRate: '—', reviewRate: '—', noteRate: '—', outcomeCompletion: '—',
      wins: 0, losses: 0, breakevens: 0,
      topSetupTypes: [], topRegimes: [], topSymbols: [], topGamma: [],
      disciplineScore, disciplineGrade,
      sessionTags: ['no-activity'],
    };
  }

  const sorted = [...entries].sort((a, b) => a.ts - b.ts);
  const sessionDate = fmtDate(sorted[0].ts);

  const viewed   = entries.filter(e => e.actionType === 'setup_viewed').length;
  const taken    = entries.filter(e => e.actionType === 'trade_taken').length;
  const skipped  = entries.filter(e => e.actionType === 'trade_skipped').length;
  const noted    = entries.filter(e => e.actionType === 'note_added').length;
  const outcomes = entries.filter(e => e.actionType === 'outcome_reviewed').length;

  const wins       = entries.filter(e => e.outcome === 'win').length;
  const losses     = entries.filter(e => e.outcome === 'loss').length;
  const breakevens = entries.filter(e => e.outcome === 'breakeven').length;

  const decided = taken + skipped;

  const symbolsMonitored = [...new Set(entries.map(e => e.symbol))].sort();

  return {
    sessionDate,
    exportedAt: new Date().toISOString(),
    symbolsMonitored,
    totalActions: entries.length,
    viewed, taken, skipped, noted, outcomes,
    selectionRate:    pct(taken, decided),
    reviewRate:       pct(outcomes, taken),
    noteRate:         pct(noted, decided),
    outcomeCompletion:pct(outcomes, taken),
    wins, losses, breakevens,
    topSetupTypes: topN(entries.map(e => e.setupType ?? null), 5),
    topRegimes:    topN(entries.map(e => e.regime ?? null), 4),
    topSymbols:    topN(entries.map(e => e.symbol), 5),
    topGamma:      topN(entries.map(e => e.gammaStatus ?? null), 3),
    disciplineScore,
    disciplineGrade,
    sessionTags: sessionTagger(entries, disciplineScore, outcomes, taken),
  };
}

// ─── Session tagger ───────────────────────────────────────────────────────────
// All tags are deterministic from data thresholds. No adaptive behavior.

export function sessionTagger(
  entries: Journal71AEntry[],
  disciplineScore: number,
  outcomes: number,
  taken: number,
): string[] {
  const tags: string[] = [];
  if (!entries.length) return ['no-activity'];

  const setups = entries.map(e => e.setupType?.toLowerCase() ?? '');
  const breakouts = setups.filter(s => s.includes('breakout')).length;
  const reverts   = setups.filter(s => s.includes('reversion') || s.includes('revert')).length;
  const total     = entries.length;
  const skipped   = entries.filter(e => e.actionType === 'trade_skipped').length;
  const noted     = entries.filter(e => e.actionType === 'note_added').length;
  const symbols   = new Set(entries.map(e => e.symbol)).size;

  if (breakouts / total >= 0.5) tags.push('breakout-heavy');
  if (reverts / total >= 0.4)   tags.push('range-bound');

  const sweepLiq = setups.filter(s => s.includes('sweep') || s.includes('liquidity')).length;
  if (sweepLiq / total >= 0.3)  tags.push('liquidity-driven');

  if (outcomes < taken && taken > 0) tags.push('review-incomplete');
  if (disciplineScore >= 70)         tags.push('disciplined');
  if (symbols >= 8)                  tags.push('watchlist-heavy');
  if (skipped > taken && taken > 0)  tags.push('selective');
  if (noted >= entries.length * 0.4) tags.push('well-annotated');

  return tags.length ? tags : ['standard-session'];
}

// ─── Executive summary ────────────────────────────────────────────────────────

export interface ExecutiveSummary {
  sessionDate:      string;
  exportedAt:       string;
  symbolCount:      number;
  actionCount:      number;
  taken:            number;
  skipped:          number;
  outcomeCompletion:string;
  disciplineGrade:  string;
  disciplineScore:  number;
  sessionTags:      string[];
  topSymbol:        string | null;
  topSetup:         string | null;
  topRegime:        string | null;
  wins:             number;
  losses:           number;
}

export function buildExecutiveSummary(s: SessionSummary): ExecutiveSummary {
  return {
    sessionDate:       s.sessionDate,
    exportedAt:        new Date().toISOString(),
    symbolCount:       s.symbolsMonitored.length,
    actionCount:       s.totalActions,
    taken:             s.taken,
    skipped:           s.skipped,
    outcomeCompletion: s.outcomeCompletion,
    disciplineGrade:   s.disciplineGrade,
    disciplineScore:   s.disciplineScore,
    sessionTags:       s.sessionTags,
    topSymbol:         s.topSymbols[0]?.value ?? null,
    topSetup:          s.topSetupTypes[0]?.value ?? null,
    topRegime:         s.topRegimes[0]?.value ?? null,
    wins:              s.wins,
    losses:            s.losses,
  };
}

// ─── Outcome report ───────────────────────────────────────────────────────────

export interface OutcomeReportRow {
  ts:            number;
  loggedAt:      string;
  symbol:        string;
  setupType:     string;
  direction:     string;
  score:         number | null;
  confidence:    number | null;
  regime:        string;
  gammaStatus:   string;
  action:        string;
  hasNote:       boolean;
  notePreview:   string;     // first 60 chars
  outcome:       string;
  hasSnapshot:   boolean;
  snapshotRank:  number | null;   // selected symbol's rank in snapshot
}

export function buildOutcomeReport(entries: Journal71AEntry[]): OutcomeReportRow[] {
  // Show all taken/skipped/outcome entries as the primary attribution report rows
  const relevant = entries
    .filter(e => ['trade_taken','trade_skipped','outcome_reviewed','setup_viewed'].includes(e.actionType))
    .sort((a, b) => b.ts - a.ts);

  return relevant.map(e => {
    const snap = e.rankedSnapshot ?? null;
    const selfRank = snap?.find(r => r.symbol === e.symbol)?.rank ?? null;

    return {
      ts:           e.ts,
      loggedAt:     fmtTs(e.ts),
      symbol:       e.symbol,
      setupType:    e.setupType?.replace(/_/g, ' ') ?? '—',
      direction:    e.direction ?? '—',
      score:        e.score,
      confidence:   e.confidence,
      regime:       e.regime ?? '—',
      gammaStatus:  e.gammaStatus ?? '—',
      action:       e.actionType.replace(/_/g, ' ').toUpperCase(),
      hasNote:      !!e.note,
      notePreview:  e.note ? e.note.slice(0, 60) + (e.note.length > 60 ? '…' : '') : '',
      outcome:      e.outcome ?? '—',
      hasSnapshot:  !!snap?.length,
      snapshotRank: selfRank,
    };
  });
}

// ─── Deterministic session narrative ─────────────────────────────────────────
// Template-driven only. No AI inference. Reads as a structural summary.

export function buildSessionNarrative(s: SessionSummary): string {
  const lines: string[] = [];

  lines.push(`Session: ${s.sessionDate} — ${s.symbolsMonitored.length} symbol${s.symbolsMonitored.length !== 1 ? 's' : ''} monitored`);

  if (s.totalActions === 0) {
    lines.push('No activity logged this session.');
    return lines.join('\n');
  }

  // Activity summary
  const decided = s.taken + s.skipped;
  lines.push(
    `${s.totalActions} actions logged: ${s.viewed} viewed, ${s.taken} taken, ${s.skipped} skipped, ${s.noted} noted.`
  );

  if (decided > 0) {
    lines.push(`Selection rate: ${s.selectionRate} (${s.taken} of ${decided} decided setups taken).`);
  }

  // Top patterns
  if (s.topSetupTypes.length) {
    const top = s.topSetupTypes[0];
    lines.push(`Dominant setup type: ${top.value.replace(/_/g, ' ')} (${top.count} instance${top.count !== 1 ? 's' : ''}).`);
  }
  if (s.topRegimes.length) {
    lines.push(`Primary regime: ${s.topRegimes[0].value}.`);
  }

  // Outcome closure
  if (s.taken > 0) {
    lines.push(`Outcome closure: ${s.outcomeCompletion} of taken setups have a recorded outcome.`);
    if (s.wins + s.losses + s.breakevens > 0) {
      lines.push(`Results: ${s.wins} win${s.wins !== 1 ? 's' : ''}, ${s.losses} loss${s.losses !== 1 ? 'es' : ''}, ${s.breakevens} breakeven.`);
    }
  }

  // Discipline
  lines.push(`Discipline score: ${s.disciplineScore}/100 (${s.disciplineGrade}).`);

  // Session character
  if (s.sessionTags.length) {
    lines.push(`Session character: ${s.sessionTags.join(', ')}.`);
  }

  return lines.join('\n');
}
